import React from 'react';
import { Select as BaseSelect } from '@mui/base/Select';
import { styled } from '@mui/material';
import { SVGS } from '../../../../../../assets/images';
import Listbox, { Option } from '../../../../../../components/shared/listbox';

interface SelectorOption {
    value: string;
    label: string;
}

interface CalendarSelectorProps {
    options: SelectorOption[];
    value?: string;
    onSelect?: (value: string) => void;
}

const StyledButton = styled('button', { shouldForwardProp: () => true })`
    margin: 0;
    background: none;
    border: none;
    width: 100%;
    padding: 0;
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;
    gap: 0.95rem;
    display: flex;
    align-items: center;
    box-shadow: none !important;
    white-space: nowrap;

    & div {
        display: flex;
        gap: 8px;
        align-items: center;
        width: 100%;
        min-width: 0;
        color: var(--text-secondary);
        font-size: 22px;
        font-style: normal;
        font-weight: 500;
        line-height: 30px;

        @media (max-width: 1000px) {
            font-size: 20px;
            line-height: 28px;
        }

        .calendar-selector-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
        }
    }

    & svg {
        flex-shrink: 0;
    }

    & svg path {
        fill: var(--text-secondary);
    }
`;

const Popup = styled('div')`
    z-index: 1300;
`;

const LimitedListbox = styled(Listbox)`
    max-height: 250px;
    overflow-y: auto;
`;

const CustomButton = React.forwardRef<
    HTMLButtonElement,
    React.PropsWithChildren<any>
>(function CustomButton({ children, ownerState, ...other }, ref) {
    return (
        <StyledButton type='button' {...other} ref={ref}>
            <div>
                <span className='calendar-selector-label'>{children}</span>
                <SVGS.KeyboardArrowDownIcon />
            </div>
        </StyledButton>
    );
});

const CalendarSelector: React.FC<CalendarSelectorProps> = ({ options, value, onSelect }) => {
    const selectedValue = value ?? options[0]?.value ?? '';

    const handleChange = (
        event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
        newValue: string | null
    ) => {
        if (!newValue) return;
        onSelect?.(newValue);
    };

    return (
        <BaseSelect
            value={selectedValue}
            onChange={handleChange}
            slots={{
                root: CustomButton,
                listbox: LimitedListbox,
                popup: Popup,
            }}
        >
            {options.map(option => (
                <Option key={option.value} value={option.value}>
                    {option.label}
                </Option>
            ))}
        </BaseSelect>
    );
};

export default React.memo(CalendarSelector);
